import { IconInfoCircle, IconX } from '@tabler/icons-react';
import { useEffect, useState } from 'react';

import { useMessages } from '@/hooks/messages';

import { archivedModelBannerMessages } from './messages';

const DISMISS_STORAGE_PREFIX = 'archived-model-banner-dismissed:';

/**
 * EWM-1036: an estimation built on an archived model stays fully editable - nothing about it is
 * read-only - so this banner is informational only.
 *
 * Dismissal is per browser session and per estimation, so the banner comes back next session
 * (the ticket requirement). sessionStorage is what gives that lifetime; localStorage would keep
 * the dismissal forever. Same mechanism as the maintenance banner (EWM-758). Reads and writes are
 * guarded because storage throws in some privacy configurations.
 */
export const ArchivedModelBanner: React.FC<{ estimationId: string }> = ({ estimationId }) => {
  const messages = useMessages(archivedModelBannerMessages);
  const dismissKey = `${DISMISS_STORAGE_PREFIX}${estimationId}`;

  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      setDismissed(sessionStorage.getItem(dismissKey) === '1');
    } catch {
      setDismissed(false);
    }
  }, [dismissKey]);

  if (dismissed) {
    return null;
  }

  const handleDismiss = () => {
    try {
      sessionStorage.setItem(dismissKey, '1');
    } catch {
      // A browser that refuses session storage just gets the banner again next render.
    }
    setDismissed(true);
  };

  return (
    <div
      role='status'
      data-testid='archived-model-banner'
      className='mx-5 mb-1 flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-100'
    >
      <IconInfoCircle aria-hidden size={20} className='shrink-0' />
      <span className='flex-1 break-words'>{messages.text()}</span>
      <button
        type='button'
        onClick={handleDismiss}
        aria-label={messages.dismiss()}
        className='shrink-0 rounded p-1 hover:bg-black/10 dark:hover:bg-white/10'
        data-testid='archived-model-banner-dismiss'
      >
        <IconX aria-hidden size={16} />
      </button>
    </div>
  );
};
